import {
    type Location,
    type BoundLocationValue,
    type ParameterValueKind,
    type PriceCalculationUnit,
    type PriceCalculationValueWithUnit,
    type Route,
    PriceCalculationParameterInfo,
    type ServiceDefinition,
} from "../api";
import { ResolvedParameterValue } from "./ResolvedParameterValue";

/**
 * Represents a text input value with specific input type and optional placeholder.
 */

export class ResolvedLocationValue extends ResolvedParameterValue<Route, BoundLocationValue> {
    readonly placeholder?: string;
    readonly help?: string;
    readonly origin: Location;
    readonly unit?: PriceCalculationUnit;
    readonly max?: number;
    /**
     * Creates a new text value with input type and optional placeholder bound to a parameter.
     *
     * @param value The text input configuration
     */
    constructor(value: Omit<BoundLocationValue, "kind">) {
        super({
            ...value,
            kind: "location",
        });
        this.placeholder = value.placeholder;
        this.help = value.help;
        this.origin = value.origin;
        this.unit = value.unit;
        this.max = value.max;
    }

    getKind(): ParameterValueKind {
        return this.kind;
    }

    /**
     * Gets the origin of the location.
     *
     * @returns The origin of the location or undefined if not set
     */
    getOrigin(): Location {
        return this.origin;
    }

    /**
     * Gets the placeholder text for the input field.
     *
     * @returns The placeholder text or undefined if not set
     */
    getPlaceholder(): string | undefined {
        return this.placeholder;
    }

    /**
     * Gets the help text for the input field.
     *
     * @returns The help text or undefined if not set
     */
    getHelp(): string | undefined {
        return this.help;
    }

    /**
     * Gets the maximum allowed distance or duration.
     *
     * @returns The maximum value or undefined if not set
     */
    getMax(): number | undefined {
        return this.max;
    }

    /**
     * Gets the unit for the calculation parameter.
     *
     * @returns The unit for the calculation parameter
     */
    getCalculationParameterParameterInfos(): PriceCalculationParameterInfo[] {
        return [
            {
                id: PriceCalculationParameterInfo.Id(this.parameterId.toString()),
                parameterId: this.parameterId,
                unit: this.unit ?? "km",
            },
        ];
    }

    getCalculationParameterValues(value: Route | undefined): Record<string, PriceCalculationValueWithUnit> {
        const unit: PriceCalculationUnit = this.unit ?? "km";
        if (value === undefined) {
            return { [this.parameterId.toString()]: { value: 0, unit } };
        }
        const calculationValue = value;
        if (typeof calculationValue === "string") {
            throw new Error("Calculation value is a string");
        }
        if (calculationValue.distance.unit === this.unit) {
            return {
                [this.parameterId.toString()]: { value: calculationValue.distance.value, unit },
            };
        }
        if (calculationValue.duration.unit === this.unit) {
            return {
                [this.parameterId.toString()]: { value: calculationValue.duration.value, unit },
            };
        }
        return {};
    }

    /**
     * Gets the view information for the parameter.
     *
     * @param values The value of the parameter
     * @returns The view information for the parameter
     */
    getViewText(value: Route | undefined): string | undefined {
        if (value === undefined) {
            return undefined;
        }
        let calculationValue = value;
        if (typeof calculationValue === "string") {
            if (calculationValue !== "" && calculationValue !== "undefined") {
                calculationValue = JSON.parse(calculationValue) as Route;
                if (calculationValue.destination === undefined) {
                    return undefined;
                }
            }
        }
        return calculationValue.destination.address;
    }

    /**
     * Creates a customer-safe version of this value.
     * This value type has no pricing data to strip.
     *
     * @param _requestPricing - The pricing mode (unused - no pricing data in this type)
     * @returns This instance (no changes needed for any pricing mode)
     */
    toCustomerValue(_requestPricing: ServiceDefinition.RequestPricing): this {
        return this;
    }

    /**
     * Creates a new text value with valid input type and optional placeholder.
     *
     * @param value The text input configuration bound to a parameter
     * @returns A new text value or throws an error if the values are invalid
     */
    static create(value: Omit<BoundLocationValue, "kind">): ResolvedLocationValue | never {
        return new ResolvedLocationValue(value);
    }
}
